import { useElementSize } from '@mantine/hooks';
import React, { RefCallback } from 'react';
import type { IconType } from 'react-icons';
import { LuArrowRightLeft, LuCoins, LuTable, LuTrendingUp } from 'react-icons/lu';

import { TAB_PANEL_PADDING_BOTTOM, TAB_PANEL_PADDING_X, useTabPanel } from '@/components/RoutingTabs';
import { SectionNav } from '@/components/SectionNav';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { cn } from '@/utils/cn';
import { useAggregatedPerformanceReport } from 'views/portfolio/reporting-queries';

import { ReportingGlossarySheet } from '../reporting/ReportingGlossarySheet';
import { ReportingHeader } from '../reporting/ReportingHeader';
import { SummaryKPICard } from '../reporting/SummaryKPICard';
import { useReportingScope } from '../reporting/useReportingScope';
import { IncomeSection } from './components/IncomeSection';
import { HoldingsSection } from './HoldingsSection';
import { PerformanceSection } from './PerformanceSection';
import { TransactionsSection } from './TransactionsSection';

// The page's sections in order. The names double as the section nav labels and the useScrollSpy
// ids, as policySectionNames do on the Investment Policy tab.
const REPORTING_SECTIONS = ['Performance', 'Holdings', 'Income & Cash Flow', 'Transactions'] as const;

// Space between the sticky bar and the content below it; must match the bar's mb-6.
const BAR_GAP_PX = 24;

// A titled section — 22px seafoam-500 icon + the Investment Policy tab's sans semibold section title
// (PolicyFeatureList's h2). The scroll margin matches the sticky filter bar so a nav click lands the
// title just below it.
const Section = ({
    title,
    icon: Icon,
    sectionRef,
    children,
}: React.PropsWithChildren<{
    title: string;
    icon: IconType;
    // From useScrollSpy: lets the section nav track and scroll to this section.
    sectionRef: RefCallback<HTMLElement>;
}>) => (
    <section ref={sectionRef} aria-label={title} className="flex scroll-mt-(--reporting-sticky-offset) flex-col gap-6">
        <div className="flex items-center gap-[11px]">
            <Icon className="text-seafoam-500 h-[22px] w-[22px]" />
            <h2 className="text-cocoa-900 m-0 text-2xl font-semibold">{title}</h2>
        </div>
        {children}
    </section>
);

/**
 * The reporting overview: a sticky filter bar, a sticky section nav that tracks and scrolls to
 * the section in view, and every household report as a section of its own.
 *
 * The Reporting tab is registered with noPadding so the tab panel (the scroll root) has no
 * padding: the sticky bar pins flush at top-0 and its border spans the full width. The bar and the
 * content below each apply the panel's usual horizontal padding themselves.
 */
export const HouseholdReportingOverviewTab = () => {
    const { householdGUID, dateRange, selectedAccountGUIDs, includeClosed } = useReportingScope();
    const { data: report, isPending } = useAggregatedPerformanceReport({
        householdGUID,
        fromInclusive: dateRange.fromInclusive,
        toInclusive: dateRange.toInclusive,
        groupByGuids: selectedAccountGUIDs,
        includeClosed,
    });

    // The bar's border-box height (padding and border included), which varies as its controls wrap.
    const { ref: barRef, height: barHeight } = useElementSize<HTMLDivElement>();
    // Where a section title lands below the scroll root's top, so a scrolled-to section sits as far
    // below the bar as the first one does. Shared by the nav's sticky top, the sections' scroll
    // margin and the scroll spy's activation line, so a clicked section is also the highlighted one.
    const stickyOffset = barHeight + BAR_GAP_PX;

    const scrollRoot = useTabPanel();
    const { activeSection, sectionRef, endRef, scrollToSection } = useScrollSpy(REPORTING_SECTIONS, {
        root: scrollRoot,
        offset: stickyOffset,
    });

    return (
        <div
            className={TAB_PANEL_PADDING_BOTTOM}
            style={{ '--reporting-sticky-offset': `${stickyOffset}px` } as React.CSSProperties}
        >
            {/* Same 1px cocoa-100 (#E5E4E1) bottom border as the tab strip above (TabsList); equal py-3
                so the controls sit centred between the two rules. */}
            {/* Mantine types its ref React 19-style (RefObject<T | null>); @types/react 18 wants RefObject<T>. */}
            <div
                ref={barRef as React.RefObject<HTMLDivElement>}
                className={cn('border-cocoa-100 sticky top-0 z-10 mb-6 border-b bg-white py-3', TAB_PANEL_PADDING_X)}
            >
                <ReportingHeader householdGUID={householdGUID} />
            </div>
            <div className={cn('flex flex-col gap-6 lg:flex-row lg:gap-14', TAB_PANEL_PADDING_X)}>
                <SectionNav
                    sections={REPORTING_SECTIONS}
                    activeSection={activeSection}
                    onSelect={scrollToSection}
                    ariaLabel="Reporting sections"
                    className="lg:top-(--reporting-sticky-offset)"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-16">
                    {/* The headline KPIs lead the page; they are not a nav section of their own. */}
                    <SummaryKPICard report={report} isLoading={isPending} />
                    <Section title="Performance" icon={LuTrendingUp} sectionRef={sectionRef('Performance')}>
                        <PerformanceSection />
                    </Section>
                    <Section title="Holdings" icon={LuTable} sectionRef={sectionRef('Holdings')}>
                        <HoldingsSection />
                    </Section>
                    <Section title="Income & Cash Flow" icon={LuCoins} sectionRef={sectionRef('Income & Cash Flow')}>
                        <IncomeSection
                            householdGUID={householdGUID}
                            selectedAccountGUIDs={selectedAccountGUIDs}
                            includeClosed={includeClosed}
                        />
                    </Section>
                    <Section title="Transactions" icon={LuArrowRightLeft} sectionRef={sectionRef('Transactions')}>
                        <TransactionsSection />
                    </Section>
                    <div ref={endRef} aria-hidden="true" className="h-px" />
                </div>
            </div>
            <ReportingGlossarySheet />
        </div>
    );
};
